import { useState, useRef } from "react";
import { formatJPY } from "@juro/shared";
import type { WorkItem, WorkItemInput } from "../../api/workItemsApi.js";
import {
  WORK_CATEGORY_LABEL, TAX_CATEGORY_LABEL, PROFESSION_LABEL,
  getDefaultTaxCategory, getDefaultIsPassThrough, getDefaultWithholdingTarget,
} from "../../lib/workItemConstants.js";

type Props = {
  item:      WorkItem;
  dragHandleProps?: Record<string, unknown>;
  onUpdate:  (id: string, patch: Partial<WorkItemInput>) => void;
  onDelete:  (id: string) => void;
  onDuplicate: (item: WorkItem) => void;
};

const PROFESSION_OPTIONS = ["judicial", "administrative", "surveyor", "common", "other"] as const;
/**
 * 行から選べるカテゴリ。
 * `withholding` / `consumption_tax` は計算で導かれるもので明細としては投影されない
 * （`work-items-to-document.ts` が null を返す）ため、手入力の選択肢から外す。
 */
const CATEGORY_OPTIONS = ["fee", "registration_tax", "stamp", "postage", "pass_through", "other"] as const;

const inputStyle: React.CSSProperties = {
  padding: "3px 6px",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  fontSize: 12,
  width: "100%",
  background: "#fff",
};

export function WorkItemRow({ item, dragHandleProps, onUpdate, onDelete, onDuplicate }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]   = useState<Partial<WorkItemInput>>({});
  // 行が編集できることに気づきにくいという指摘への対応。ホバー時だけ ✎ ボタンと
  // 背景色で「クリックすると編集できる」ことを示す（常時表示だと ⊕/× と並んで煩雑になるため）
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  function startEdit() {
    setEditing(true);
    setDraft({});
  }

  /** 複数フィールドを一度に更新する（カテゴリのように派生値を伴う変更で使う） */
  function patchMany(fields: Partial<WorkItemInput>) {
    const next = { ...draft, ...fields };
    setDraft(next);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onUpdate(item.id, next);
    }, 600);
  }

  function patch(field: keyof WorkItemInput, value: unknown) {
    patchMany({ [field]: value } as Partial<WorkItemInput>);
  }

  function commitEdit() {
    clearTimeout(timerRef.current);
    if (Object.keys(draft).length > 0) {
      onUpdate(item.id, draft);
    }
    setEditing(false);
    setDraft({});
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { setEditing(false); setDraft({}); }
    // IME変換確定のEnterは拾わない（日本語入力中に確定してしまうのを防ぐ）
    if (e.key === "Enter" && editing && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commitEdit();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === "d") { e.preventDefault(); onDuplicate(item); }
  }

  /**
   * 行内の別フィールドへクリック/Tabで移動しただけなのに、各フィールドの onBlur が
   * 無条件に commitEdit（＝編集モード終了）していたため、業務名欄から単価欄へ移ろうとすると
   * 単価欄が触れる前に編集モードが閉じてしまうバグがあった。
   * フォーカスが行の外へ出たときだけ確定するよう、tr レベルで relatedTarget を見て判定する。
   */
  function handleRowBlur(e: React.FocusEvent<HTMLTableRowElement>) {
    if (!editing) return;
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) {
      commitEdit();
    }
  }

  const qty        = (draft.quantity   ?? item.quantity);
  const unitPrice  = (draft.unit_price ?? item.unit_price);
  const calcAmount = qty * unitPrice;

  const tdStyle: React.CSSProperties = {
    padding: "6px 8px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 12,
    verticalAlign: "middle",
  };

  return (
    <tr onDoubleClick={startEdit} onKeyDown={handleKeyDown} onBlur={handleRowBlur} tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={editing ? undefined : "ダブルクリック、または ✎ アイコンで編集できます"}
        style={{
          cursor: editing ? "default" : "pointer",
          background: editing ? "#fafffe" : hovered ? "#f0fdfa" : "transparent",
        }}>

      {/* ドラッグハンドル */}
      <td style={{ ...tdStyle, width: 20, color: "#cbd5e1", cursor: "grab" }}
          {...(dragHandleProps ?? {})}>
        ⠿
      </td>

      {/* 業務名 */}
      <td style={{ ...tdStyle, minWidth: 140 }}>
        {editing ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {/* 業種は入力時に選ばせない代わりに、後から行で変更できるようにする（設計 §6.2 D8） */}
            <select
              value={String(draft.profession ?? item.profession)}
              title="業種"
              style={{ ...inputStyle, width: 78, flex: "0 0 auto" }}
              onChange={(e) => {
                // 業種を変えるとグループが変わり行が再配置されるため、その場で確定する
                clearTimeout(timerRef.current);
                const next = { ...draft, profession: e.target.value };
                setDraft({});
                setEditing(false);
                onUpdate(item.id, next);
              }}
            >
              {PROFESSION_OPTIONS.map((p) => (
                <option key={p} value={p}>{PROFESSION_LABEL[p] ?? p}</option>
              ))}
            </select>
            <input
              autoFocus
              style={inputStyle}
              value={String(draft.title ?? item.title)}
              placeholder="業務名"
              onChange={(e) => patch("title", e.target.value)}
            />
          </div>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 500, color: "#0f172a" }}>{item.title}</span>
            {hovered && (
              <span style={{ fontSize: 11, color: "#0f766e" }} aria-hidden="true">✎</span>
            )}
          </span>
        )}
      </td>

      {/* 数量 */}
      <td style={{ ...tdStyle, width: 56 }}>
        {editing ? (
          <input type="number" min={0} style={{ ...inputStyle, width: 50, textAlign: "right" }}
            value={Number(draft.quantity ?? item.quantity)}
            onChange={(e) => patch("quantity", Number(e.target.value))}
          />
        ) : (
          <span style={{ color: "#475569" }}>{item.quantity}</span>
        )}
      </td>

      {/* 単価 */}
      <td style={{ ...tdStyle, width: 96 }}>
        {editing ? (
          <input type="number" min={0} style={{ ...inputStyle, width: 88, textAlign: "right" }}
            value={Number(draft.unit_price ?? item.unit_price)}
            onChange={(e) => patch("unit_price", Number(e.target.value))}
          />
        ) : (
          <span style={{ color: "#475569" }}>{formatJPY(item.unit_price)}</span>
        )}
      </td>

      {/* 金額（読み取り専用） */}
      <td style={{ ...tdStyle, width: 96, textAlign: "right", fontWeight: 600, color: "#0f172a" }}>
        {formatJPY(editing ? calcAmount : item.amount)}
      </td>

      {/* 税区分 */}
      <td style={{ ...tdStyle, width: 72 }}>
        {editing ? (
          <select style={{ ...inputStyle, width: 68 }}
            value={String(draft.tax_category ?? item.tax_category)}
            onChange={(e) => patch("tax_category", e.target.value)}>
            {Object.entries(TAX_CATEGORY_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {TAX_CATEGORY_LABEL[item.tax_category as keyof typeof TAX_CATEGORY_LABEL] ?? item.tax_category}
          </span>
        )}
      </td>

      {/* 源泉 */}
      <td style={{ ...tdStyle, width: 40, textAlign: "center" }}>
        <input type="checkbox"
          checked={Boolean(draft.withholding_target ?? item.withholding_target)}
          onChange={(e) => {
            patch("withholding_target", e.target.checked);
            if (!editing) { clearTimeout(timerRef.current); onUpdate(item.id, { withholding_target: e.target.checked }); }
          }}
          style={{ cursor: "pointer" }}
        />
      </td>

      {/* 立替 */}
      <td style={{ ...tdStyle, width: 40, textAlign: "center" }}>
        <input type="checkbox"
          checked={Boolean(draft.is_pass_through ?? item.is_pass_through)}
          onChange={(e) => {
            patch("is_pass_through", e.target.checked);
            if (!editing) { clearTimeout(timerRef.current); onUpdate(item.id, { is_pass_through: e.target.checked }); }
          }}
          style={{ cursor: "pointer" }}
        />
      </td>

      {/* カテゴリ
          単なる表示ラベルではなく、請求書のどの列へ投影されるかを決める値
          （fee/other→報酬額、registration_tax/stamp/postage/pass_through→登録税・印紙税。
          `apps/api/src/lib/work-items-to-document.ts` の switch）。
          入力時には聞かない（設計 §6.2 D8）代わりに、行から必ず直せるようにしておく。 */}
      <td style={{ ...tdStyle, width: 84 }}>
        {editing ? (
          <select style={{ ...inputStyle, width: 80 }}
            value={String(draft.category ?? item.category)}
            title="カテゴリ（報酬額の列か、登録税・印紙税の列かが決まります）"
            onChange={(e) => {
              // 税区分・源泉・立替はカテゴリからの導出値なので必ず同時に更新する
              const category   = e.target.value;
              const profession = String(draft.profession ?? item.profession);
              patchMany({
                category,
                tax_category:       getDefaultTaxCategory(category),
                withholding_target: getDefaultWithholdingTarget(category, profession),
                is_pass_through:    getDefaultIsPassThrough(category),
              });
            }}>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{WORK_CATEGORY_LABEL[c] ?? c}</option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 11, color: "#64748b" }}
                title="ダブルクリックで変更できます（登録免許税・印紙税などはここを変えると請求書の列が変わります）">
            {WORK_CATEGORY_LABEL[item.category as keyof typeof WORK_CATEGORY_LABEL] ?? item.category}
          </span>
        )}
      </td>

      {/* 編集・削除・複製 */}
      <td style={{ ...tdStyle, width: 72, textAlign: "right" }}>
        {hovered && !editing && (
          <button
            onClick={(e) => { e.stopPropagation(); startEdit(); }}
            title="編集（行のダブルクリックでも可）"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#0f766e", fontSize: 13, padding: "0 2px" }}>
            ✎
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(item); }}
          title="複製"
          style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 13, padding: "0 2px" }}>
          ⊕
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); if (confirm("削除しますか？")) onDelete(item.id); }}
          title="削除"
          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 14, padding: "0 2px" }}>
          ×
        </button>
      </td>
    </tr>
  );
}

export const PROFESSION_COLORS: Record<string, string> = {
  judicial:       "#0f766e",
  administrative: "#7c3aed",
  surveyor:       "#b45309",
  common:         "#475569",
  other:          "#64748b",
};

// 無名 export for convenient access
export { PROFESSION_LABEL, WORK_CATEGORY_LABEL, TAX_CATEGORY_LABEL };
